import { useEffect, useRef } from "react";

import { debounce } from "@s-libs/micro-dash";
import { useRouter } from "next/router";

import { BannerMode } from "@/types/BannerMode.interface";
import ContentLevel from "@/types/models/ContentLevel.interface";
import contentTypes from "@/view/constants/contentTypes";
import useScrollDepthTracking from "@/view/utils/useScrollDepthTracking";
import ArticleDisplay from "./ArticleDisplay";
import VideoDisplay from "./VideoDisplay";

interface ContentDisplayProps {
  bannerMode: BannerMode;
  content: any; // TODO: replace with proper type
  contentMetadata: any; // TODO: replace with proper type
  currentLevel: ContentLevel | undefined;
  isVocabularySupportEnabled: boolean;
  keyTermDataContentId: string;
  legacyArticleId: number;
  matchedAssignments: any[]; // TODO: replace with proper type
  rawText: any; // TODO: replace with proper type
  readAloudButtonRef: any; // TODO: replace with proper type
  shouldShowMobileAction: boolean;
  toggleVocabularySupport: (value: boolean) => void;
  user: any; // TODO: replace with proper type
}

const ContentDisplay = ({
  bannerMode,
  content,
  contentMetadata,
  currentLevel,
  isVocabularySupportEnabled,
  keyTermDataContentId,
  legacyArticleId,
  matchedAssignments,
  rawText,
  readAloudButtonRef,
  shouldShowMobileAction,
  toggleVocabularySupport,
  user,
}: ContentDisplayProps) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;
  const contentDisplayRef = useRef();
  const isArticle = content.content_type === contentTypes.ARTICLE;

  const { handleArticleReading } = useScrollDepthTracking({
    contentId: levelId,
    userId: user.unique_id,
  });

  const handleScroll = (event: any) =>
    handleArticleReading({
      scrollTop: event?.target?.scrollTop,
      contentDisplayRef,
    });
  const debouncedHandleScroll = debounce(handleScroll, 75);

  useEffect(() => {
    const mainElement = document.getElementById("__next");

    if (isArticle) {
      mainElement?.addEventListener("scroll", debouncedHandleScroll);
    }

    return () =>
      mainElement?.removeEventListener("scroll", debouncedHandleScroll);
  }, [handleScroll]);

  switch (content.content_type) {
    case contentTypes.ARTICLE:
      return (
        <ArticleDisplay
          bannerMode={bannerMode}
          content={content}
          contentDisplayRef={contentDisplayRef}
          contentMetadata={contentMetadata}
          currentLevel={currentLevel}
          isVocabularySupportEnabled={isVocabularySupportEnabled}
          keyTermDataContentId={keyTermDataContentId}
          legacyArticleId={legacyArticleId}
          matchedAssignments={matchedAssignments}
          rawText={rawText}
          readAloudButtonRef={readAloudButtonRef}
          shouldShowMobileActions={shouldShowMobileAction}
          toggleVocabularySupport={toggleVocabularySupport}
          user={user}
        />
      );
    case contentTypes.INTERACTIVE_VIDEO:
      return (
        <VideoDisplay
          bannerMode={bannerMode}
          content={content}
          contentMetadata={contentMetadata}
          matchedAssignments={matchedAssignments}
          user={user}
        />
      );
    default:
      return null;
  }
};

export default ContentDisplay;
